// SCR-RPT-01 and SCR-RPT-03 (frozen mockups-rpt-v1): the owner's dashboard (ADR-126). Analysis only: nothing is created
// here (DASH-01). The period (opens on today) and the sales basis drive every figure; alerts only open the page that
// handles them (DASH-02); charts sit in tabs Sales · Stock · Money (ADR-087), each followed by its numbers (DASH-03); the
// result is before commissions and owner shares, never "net profit" (GEN-12, ADR-124).
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Button,
  Chips,
  ColumnChart,
  DataTable,
  EmptyState,
  List,
  ListRow,
  Notice,
  Section,
  SkeletonList,
  SummaryCard,
  Tabs,
} from '../../design/kit';
import type { IconName } from '../../design/icons';
import { REPORTS, loadDashboard, loadDebtReport, type Basis, type Dashboard, type Period } from '../../data/reports';
import { Code, Name } from '../../i18n/Isolate';
import type { Language } from '../../i18n';
import { formatDate, formatMoney } from '../../lib/format';
import { useSession } from '../../app/session';
import { useTab } from '../catalog/common';

const TABS = ['dsales', 'dstock', 'dmoney'] as const;
type Tab = (typeof TABS)[number];

export function useReportWords() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  return {
    t,
    lang,
    money: (v: number) => formatMoney(v, lang),
    day: (d: string) => formatDate(d, lang),
    basisLabel: (b: Basis) => t(b === 'payment' ? 'basis.pay' : 'basis.sale'),
  };
}

export function BasisChips({ value, onChange }: { value: Basis; onChange: (b: Basis) => void }) {
  const { t } = useTranslation();
  return (
    <Chips
      label={t('basis.sale')}
      items={[
        { id: 'sale', label: t('basis.sale') },
        { id: 'payment', label: t('basis.pay') },
      ]}
      selected={[value]}
      onToggle={(id) => onChange(id as Basis)}
    />
  );
}

export function DashboardPage() {
  const { t, lang, money, day } = useReportWords();
  const { name } = useSession();
  const [period, setPeriod] = useState<Period>('today');
  const [basis, setBasis] = useState<Basis>('sale');
  const [tab, setTab] = useTab(TABS, 'dsales');
  const data = useQuery({
    queryKey: [...REPORTS, 'dashboard', period, basis],
    queryFn: () => loadDashboard(period, basis),
  });
  const d = data.data;
  return (
    <>
      <p className="muted">
        {t('db.hello', { n: name })} · <bdi className="yv-nowrap">{d ? formatDate(d.to, lang) : ''}</bdi>
      </p>
      <Tabs
        label={t('per.today')}
        value={period}
        onChange={(v) => setPeriod(v as Period)}
        items={[
          { id: 'today', label: t('per.today') },
          { id: 'week', label: t('per.week') },
          { id: 'month', label: t('per.month') },
        ]}
      />
      <BasisChips value={basis} onChange={setBasis} />
      {data.isError ? (
        <Notice tone="danger">{t('err.generic')}</Notice>
      ) : !d ? (
        <SkeletonList label={t('loading')} />
      ) : (
        <>
          <SummaryCard
            label={t('db.cash')}
            value={money(d.cash)}
            grid
            figures={[
              { label: t('db.sales'), value: money(d.sales) },
              { label: t('db.collected'), value: money(d.collected) },
              { label: t('db.unpaid'), value: money(d.unpaid) },
            ]}
          />
          <Alerts d={d} />
          <Tabs
            label={t('p.dashboard')}
            value={tab}
            onChange={(v) => setTab(v as Tab)}
            items={TABS.map((id) => ({ id, label: t(`tab.${id}`) }))}
          />
          {tab === 'dsales' && <SalesCharts d={d} />}
          {tab === 'dstock' && <StockCharts d={d} />}
          {tab === 'dmoney' && <MoneyCharts d={d} />}
          <p className="muted">
            {day(d.from)} – {day(d.to)}
          </p>
        </>
      )}
    </>
  );
}

/** DASH-02 / GEN-13: one row per problem with a button that only opens the page that handles it. */
function Alerts({ d }: { d: Dashboard }) {
  const { t, money } = useReportWords();
  const navigate = useNavigate();
  const a = d.alerts;
  const rows: {
    id: string;
    icon: IconName;
    tone: 'warning' | 'danger' | 'neutral';
    title: string;
    sub: string;
    to: string;
    show: boolean;
  }[] = [
    {
      id: 'low',
      icon: 'alert',
      tone: 'warning',
      title: t('al.low'),
      sub: t('al.low.n', { n: a.low_stock }),
      to: '/purchasing',
      show: a.low_stock > 0,
    },
    {
      id: 'overdue',
      icon: 'clock',
      tone: 'danger',
      title: t('al.overdue'),
      sub: t('al.overdue.n', { n: a.overdue, a: money(a.overdue_amount) }),
      to: '/sales',
      show: a.overdue > 0,
    },
    {
      id: 'pre',
      icon: 'box',
      tone: 'neutral',
      title: t('al.pre'),
      sub: t('al.pre.n', { n: a.pre_orders }),
      to: '/orders',
      show: a.pre_orders > 0,
    },
    {
      id: 'late',
      icon: 'cart',
      tone: 'warning',
      title: t('al.late'),
      sub: t('al.late.n', { n: a.late_orders }),
      to: '/purchasing',
      show: a.late_orders > 0,
    },
    {
      id: 'lots',
      icon: 'tag',
      tone: 'danger',
      title: t('al.lots'),
      sub: t('al.lots.n', { n: a.lots, p: a.lot_pieces }),
      to: '/inventory',
      show: a.lots > 0,
    },
    {
      id: 'ledger',
      icon: 'alert',
      tone: 'danger',
      title: t('al.ledger'),
      sub: t('al.ledger.n', { n: a.ledger }),
      to: '/inventory/reconcile',
      show: a.ledger > 0,
    },
  ];
  const shown = rows.filter((r) => r.show);
  return (
    <Section title={t('db.attention')}>
      {shown.length === 0 ? (
        <EmptyState icon="check" title={t('db.calm')} text={t('db.calm.text')} />
      ) : (
        <List label={t('db.attention')}>
          {shown.map((r) => (
            <ListRow
              key={r.id}
              testId={`alert-${r.id}`}
              lead={{ icon: r.icon }}
              leadTone={r.tone}
              title={r.title}
              sub={r.sub}
              actions={
                <Button pill onClick={() => navigate(r.to)}>
                  {t('al.open')}
                </Button>
              }
            />
          ))}
        </List>
      )}
    </Section>
  );
}

function SalesCharts({ d }: { d: Dashboard }) {
  const { t, money, day, basisLabel } = useReportWords();
  const basis = basisLabel(d.basis);
  const first = d.series[0]?.day ?? d.from;
  const last = d.series[d.series.length - 1]?.day ?? d.to;
  return (
    <>
      <ColumnChart
        title={t('ch.sales')}
        basis={basis}
        summary={`${t('ch.sales')}: ${money(d.sales)}`}
        values={d.series.map((s) => s.sales)}
        second={d.series.map((s) => s.collected)}
        axis={[day(first), day(last)]}
        legend={[t('ch.k.sales'), t('ch.k.collected')]}
      />
      <DataTable
        label={t('ch.sales')}
        columns={[
          { key: 'day', label: t('col.date') },
          { key: 'sales', label: t('ch.k.sales'), num: true },
          { key: 'collected', label: t('ch.k.collected'), num: true },
        ]}
        rows={d.series.map((s) => ({
          key: s.day,
          cells: [<bdi className="yv-nowrap">{day(s.day)}</bdi>, money(s.sales), money(s.collected)],
        }))}
        total={[t('rp.total'), money(d.sales), money(d.collected)]}
      />
      {d.by_truck.length > 0 && (
        <>
          <BarChart
            title={t('ch.bytruck')}
            basis={basis}
            summary={d.by_truck.map((x) => `${x.truck} ${money(x.sales)}`).join(', ')}
            bars={d.by_truck.map((x) => ({
              key: x.truck,
              label: <Code>{x.truck}</Code>,
              value: x.sales,
              text: money(x.sales),
            }))}
          />
          <DataTable
            label={t('ch.bytruck')}
            columns={[
              { key: 'truck', label: t('rp.truck') },
              { key: 'sales', label: t('ch.k.sales'), num: true },
              { key: 'margin', label: t('ch.k.margin'), num: true },
            ]}
            rows={d.by_truck.map((x) => ({
              key: x.truck,
              cells: [<Code>{x.truck}</Code>, money(x.sales), money(x.margin)],
            }))}
          />
        </>
      )}
      {d.best.length > 0 && (
        <BarChart
          title={t('ch.best')}
          basis={basis}
          summary={d.best.map((x) => `${x.name} ${money(x.sales)}`).join(', ')}
          bars={d.best.map((x) => ({
            key: x.product,
            label: <Name>{x.name}</Name>,
            value: x.sales,
            text: money(x.sales),
          }))}
        />
      )}
    </>
  );
}

function StockCharts({ d }: { d: Dashboard }) {
  const { t } = useReportWords();
  const m = d.moves;
  const rows: [string, number][] = [
    ['mv.received', m.received],
    ['mv.loaded', m.loaded],
    ['mv.sold', m.sold],
    ['mv.returned', m.returned],
    ['mv.written', m.written_off],
  ];
  return (
    <>
      <BarChart
        title={t('ch.moves')}
        summary={rows.map(([k, v]) => `${t(k)} ${v}`).join(', ')}
        bars={rows.map(([k, v]) => ({ key: k, label: t(k), value: v, text: t('piece.n', { n: v }) }))}
      />
      <DataTable
        label={t('ch.moves')}
        columns={[
          { key: 'name', label: t('col.name') },
          { key: 'pieces', label: t('col.pieces'), num: true },
        ]}
        rows={rows.map(([k, v]) => ({ key: k, cells: [t(k), String(v)] }))}
      />
    </>
  );
}

/** SCR-RPT-03: the result before commissions and the value card, then result, debt ageing and cash charts. */
function MoneyCharts({ d }: { d: Dashboard }) {
  const { t, money, day, basisLabel } = useReportWords();
  const p = d.profit;
  const debt = useQuery({
    queryKey: [...REPORTS, 'debt', 'all'],
    queryFn: () =>
      loadDebtReport({
        from: d.from,
        to: d.to,
        basis: d.basis,
        truck: null,
        seller: null,
        product: null,
        customer: null,
      }),
  });
  const step = (k: string, v: string, strong = false) => (
    <ListRow key={k} testId={`profit-${k}`} title={strong ? <b>{t(k)}</b> : t(k)} value={strong ? <b>{v}</b> : v} />
  );
  return (
    <>
      <Section title={t('pr.title')}>
        <List label={t('pr.title')}>
          {[
            step('pr.net', money(p.net_sales)),
            step('pr.cogs', `− ${money(p.cogs)}`),
            step('pr.gross', money(p.gross), true),
            step('pr.exp', `− ${money(p.expenses)}`),
            step('pr.shrink', `− ${money(p.shrinkage)}`),
            step('pr.result', money(p.result), true),
          ]}
        </List>
      </Section>
      <Notice tone="info">{t('pr.note')}</Notice>
      <Section title={t('lv.title')}>
        <List label={t('lv.title')}>
          <ListRow lead={{ icon: 'layers' }} title={t('lv.stock')} value={money(d.value.stock)} />
          <ListRow lead={{ icon: 'alert' }} title={t('lv.damaged')} value={money(d.value.damaged)} />
          <ListRow lead={{ icon: 'users' }} title={t('lv.debt')} value={money(d.value.debt)} />
          <ListRow lead={{ icon: 'cash' }} title={t('lv.capital')} value={money(d.value.capital)} />
        </List>
      </Section>
      <ColumnChart
        title={t('ch.result')}
        basis={basisLabel(d.basis)}
        summary={`${t('ch.result')}: ${money(p.result)}`}
        values={d.series.map((s) => s.result)}
        axis={[day(d.from), day(d.to)]}
      />
      <BarChart
        title={t('ch.aging')}
        summary={`${t('ag.due')} ${money(d.ageing.due)}, ${t('ag.30')} ${money(d.ageing.up_to_30)}, ${t('ag.over')} ${money(d.ageing.over_30)}`}
        bars={[
          { key: 'due', label: t('ag.due'), value: d.ageing.due, text: money(d.ageing.due) },
          { key: '30', label: t('ag.30'), value: d.ageing.up_to_30, text: money(d.ageing.up_to_30) },
          { key: 'over', label: t('ag.over'), value: d.ageing.over_30, text: money(d.ageing.over_30), tone: 'danger' },
        ]}
      />
      {debt.data && debt.data.length > 0 && <DebtTable rows={debt.data} />}
      <BarChart
        title={t('ch.cashby')}
        summary={d.cash_by.map((c) => `${c.truck ?? t(`cash.${c.place}`)} ${money(c.balance)}`).join(', ')}
        bars={d.cash_by.map((c, i) => ({
          key: `${c.place}-${c.truck ?? i}`,
          label: c.truck ? <Code>{c.truck}</Code> : t(c.place === 'garage' ? 'loc.garage' : 'cash.bank'),
          value: c.balance,
          text: money(c.balance),
        }))}
      />
    </>
  );
}

export function DebtTable({
  rows,
}: {
  rows: { customer_id: string; name: string; due: number; up_to_30: number; over_30: number; total: number }[];
}) {
  const { t, money } = useReportWords();
  const sum = (k: 'due' | 'up_to_30' | 'over_30' | 'total') => money(rows.reduce((s, r) => s + r[k], 0));
  return (
    <DataTable
      label={t('ch.aging')}
      columns={[
        { key: 'name', label: t('rp.customer') },
        { key: 'due', label: t('ag.due'), num: true },
        { key: '30', label: t('ag.30'), num: true },
        { key: 'over', label: t('ag.over'), num: true },
        { key: 'total', label: t('ag.total'), num: true },
      ]}
      rows={rows.map((r) => ({
        key: r.customer_id,
        cells: [<Name>{r.name}</Name>, money(r.due), money(r.up_to_30), money(r.over_30), money(r.total)],
      }))}
      total={[t('rp.total'), sum('due'), sum('up_to_30'), sum('over_30'), sum('total')]}
      warn={(row, col) => col === 'over' && (rows.find((r) => r.customer_id === row)?.over_30 ?? 0) > 0}
    />
  );
}
